import type { IFedopsLogger } from '../FedopsLogger/FedopsLogger';
import type { IPlatformPanorama } from '../../types/PlatformTypes';
import { Services } from '../../ioc/services';
import { inject, injectable } from '@wix/typesafe-inversify';
import type * as Panorama from '@wix/yoshi-flow-editor/panorama';
import assert from 'assert';
import type { ITransaction } from './ITransaction';
import type { ICommonSessionDataProvider } from './CommonSessionDataProvider/ICommonSessionDataProvider';
import type { IConsoleLogger } from '../ConsoleLogger/IConsoleLogger';
import { toKnownError } from './ToKnownError';
import type { ErrorWithReportedFlag } from './ErrorReportedSymbol';
import { ErrorReported } from './ErrorReportedSymbol';
import { v4 } from 'uuid';
import { isExpectedError } from './expectedErrors';

type IPanoramaTransaction = ReturnType<
  Panorama.PanoramaClientForComponent['transaction']
>;
type ITransactionUserData = Parameters<IPanoramaTransaction['start']>[0];

@injectable()
export class Transaction implements ITransaction {
  private correlationId: string = v4();

  constructor(
    @inject(Services.FedopsLogger)
    private readonly fedopsLogger: IFedopsLogger,
    @inject(Services.PlatformPanorama)
    private readonly platformPanorama: IPlatformPanorama,
    @inject(Services.CommonSessionDataProvider)
    private readonly commonSessionDataProvider: ICommonSessionDataProvider,
    @inject(Services.ConsoleLogger)
    private readonly consoleLogger: IConsoleLogger,
  ) {}

  private _name: string | undefined;

  private get name(): string {
    assert(!!this._name, 'init was not called for service. no name');

    return this._name;
  }

  private _panoramaTransaction: IPanoramaTransaction | undefined;

  private get panoramaTransaction(): IPanoramaTransaction {
    assert(
      this._panoramaTransaction,
      'init was not called for service. no panoramaTransaction',
    );
    return this._panoramaTransaction;
  }

  public init(name: string) {
    assert(!this._name, 'Wrong init flow, transactions cannot be reused');

    this._name = name;
    this._panoramaTransaction = this.platformPanorama.transaction(name, {
      id: this.correlationId,
    });
    return this;
  }

  /**
   * Executes a function within the transaction lifecycle
   * Automatically handles starting, stopping, and error reporting
   * @param action The function to execute within the transaction
   * @returns The result of the action function
   */
  async execute<T>(action: () => Promise<T>): Promise<T> {
    this.start();
    try {
      const result = await action();
      this.stop();
      return result;
    } catch (error) {
      if (isExpectedError(error)) {
        this.stop();
        throw error;
      }

      await this.reportError(error);
      throw error;
    }
  }

  /**
   * Starts the transaction
   */
  public start(data?: ITransactionUserData): ITransaction {
    this.fedopsLogger.interactionStarted(this.name);
    this.panoramaTransaction.start({
      ...this.getCommonData(),
      ...data,
    });
    return this;
  }

  /**
   * Stops the transaction
   */
  public stop(data?: ITransactionUserData): ITransaction {
    this.fedopsLogger.interactionEnded(this.name);
    this.panoramaTransaction.finish({
      ...this.getCommonData(),
      ...data,
    });
    return this;
  }

  /**
   * Reports an error that occurred during the transaction
   * Note: This does not end the transaction
   */
  public async reportError(unknownError: unknown): Promise<void> {
    const error: ErrorWithReportedFlag = toKnownError(unknownError);
    if (error[ErrorReported] === true) {
      return;
    }
    this.consoleLogger.error(error);
    await this.reportErrorToPanorama(error);

    if (error && typeof error === 'object') {
      error[ErrorReported] = true;
    }
  }

  private async reportErrorToPanorama(error: Error) {
    const sessionData = this.getCommonData();
    const { user, ...otherTraceData } =
      await this.commonSessionDataProvider.getEditorTraceData();

    const context = {
      'Wix User': user,
      'Session Data': sessionData,
      ...otherTraceData,
    };
    this.platformPanorama
      .errorMonitor({
        data: {
          environment: process.env.NODE_ENV,
          tags: {
            transactionName: this.name,
          },
          context,
        },
      })
      .reportError(error, {});
  }

  private getCommonData() {
    return {
      correlationId: this.correlationId,
      ...this.commonSessionDataProvider.getCommonSessionData(),
    };
  }
}
